import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Lock, Mic, BookOpen, Star, Volume2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1560785496-3c9d27877182?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjaGlsZCUyMHN0dWR5aW5nJTIwY2FsbXxlbnwwfHx8fDE3ODcyNTMzOTV8MA&ixlib=rb-4.1.0&q=85";

function Feature({ icon: Icon, title, desc, testId }) {
  return (
    <div className="tm-card flex gap-4 items-start" data-testid={testId}>
      <div
        className="flex-shrink-0 rounded-xl p-3"
        style={{ background: "var(--tm-bg-3)", color: "var(--tm-brand)" }}
      >
        <Icon size={24} strokeWidth={2.2} />
      </div>
      <div>
        <h3 className="text-lg font-bold mb-1">{title}</h3>
        <p className="text-base text-[color:var(--tm-ink-3)] leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const startCta = () => nav(user ? "/dashboard" : "/register");

  return (
    <main className="min-h-screen" data-testid="landing-page">
      {/* Top bar */}
      <header
        className="border-b-2"
        style={{ borderColor: "var(--tm-border)", background: "#fff" }}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="inline-block w-9 h-9 rounded-lg text-white text-center font-black leading-9"
              style={{ background: "var(--tm-brand)", fontFamily: "Nunito, sans-serif" }}
            >
              T
            </span>
            <span
              className="text-xl font-extrabold"
              style={{ fontFamily: "Nunito, sans-serif" }}
            >
              ThinkMyWay
            </span>
          </div>
          <nav className="flex items-center gap-2">
            <Link to="/login" className="tm-btn tm-btn-ghost" data-testid="landing-login-link">
              Sign in
            </Link>
            <Link to="/register" className="tm-btn tm-btn-primary" data-testid="landing-signup-link">
              Get started
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 pt-14 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="tm-chip mb-5" data-testid="landing-eyebrow">
            <Star size={14} /> Built with autistic learners in mind
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6"
            style={{ fontFamily: "Nunito, sans-serif" }}
            data-testid="landing-title"
          >
            Homework help that speaks{" "}
            <span style={{ color: "var(--tm-brand)" }}>your child's language.</span>
          </h1>
          <p className="text-lg leading-relaxed mb-8 max-w-xl text-[color:var(--tm-ink-2)]">
            ThinkMyWay uses your child's IEP to guide every AI response. Tasks are
            broken into small numbered steps in simple, literal language — no
            surprises, no idioms, just calm and clear help.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={startCta} className="tm-btn tm-btn-primary" data-testid="landing-cta-primary">
              {user ? "Open dashboard" : "Create free account"}
            </button>
            <Link to="/login" className="tm-btn tm-btn-outline" data-testid="landing-cta-secondary">
              I already have an account
            </Link>
          </div>
          <ul className="mt-8 space-y-2">
            <li className="flex items-center gap-2 text-[color:var(--tm-ink-2)]">
              <CheckCircle2 size={18} style={{ color: "var(--tm-success)" }} /> Numbered steps, one at a time
            </li>
            <li className="flex items-center gap-2 text-[color:var(--tm-ink-2)]">
              <CheckCircle2 size={18} style={{ color: "var(--tm-success)" }} /> Voice input and read-aloud
            </li>
            <li className="flex items-center gap-2 text-[color:var(--tm-ink-2)]">
              <Lock size={18} style={{ color: "var(--tm-success)" }} /> IEP files encrypted with AES-256
            </li>
          </ul>
        </div>
        <div className="relative">
          <div
            className="rounded-3xl overflow-hidden border-2"
            style={{ borderColor: "var(--tm-border)" }}
          >
            <img
              src={HERO_IMG}
              alt="A child writing calmly at a desk"
              className="w-full h-[420px] object-cover"
              loading="lazy"
            />
          </div>
          <div
            className="absolute -bottom-4 -left-4 tm-card"
            style={{ background: "#fff", maxWidth: 260 }}
          >
            <p className="text-sm font-bold mb-1" style={{ fontFamily: "Nunito, sans-serif" }}>
              Step 3 of 4
            </p>
            <p className="text-sm text-[color:var(--tm-ink-3)]">
              Great work. Now add the two numbers you circled.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section
        className="border-y-2 py-14"
        style={{ borderColor: "var(--tm-border)", background: "var(--tm-bg-2)" }}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-bold mb-2"
            style={{ fontFamily: "Nunito, sans-serif" }}
          >
            Made for how your child learns
          </h2>
          <p className="text-[color:var(--tm-ink-3)] mb-8 max-w-2xl">
            Every feature is designed to reduce sensory load and cognitive overwhelm.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Feature
              icon={BookOpen}
              title="Personalized to their IEP"
              desc="Upload the IEP once. Reading level, goals, and accommodations shape every reply."
              testId="feature-iep"
            />
            <Feature
              icon={Mic}
              title="Voice input for non-readers"
              desc="Tap the microphone and speak. We turn speech into text automatically."
              testId="feature-voice-in"
            />
            <Feature
              icon={Volume2}
              title="Read aloud, on demand"
              desc="Every response can be read in a calm, clear voice — never auto-plays."
              testId="feature-voice-out"
            />
            <Feature
              icon={CheckCircle2}
              title="Small numbered steps"
              desc="Tasks are broken into 1, 2, 3. One idea at a time. Predictable and calm."
              testId="feature-steps"
            />
            <Feature
              icon={Star}
              title="Lessons about what they love"
              desc="Dinosaurs? Space? Sports? Bite-sized lessons matched to their reading level."
              testId="feature-interests"
            />
            <Feature
              icon={Lock}
              title="Private and encrypted"
              desc="IEP documents are encrypted with AES-256 before they leave your device."
              testId="feature-security"
            />
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16 text-center">
        <h2
          className="text-2xl sm:text-3xl font-bold mb-3"
          style={{ fontFamily: "Nunito, sans-serif" }}
        >
          Ready when you are.
        </h2>
        <p className="text-[color:var(--tm-ink-3)] mb-6">
          Create a free parent or teacher account to add your first student.
        </p>
        <button onClick={startCta} className="tm-btn tm-btn-primary" data-testid="landing-cta-bottom">
          {user ? "Open dashboard" : "Create free account"}
        </button>
      </section>

      <footer
        className="border-t-2 py-6 text-center text-sm text-[color:var(--tm-ink-3)]"
        style={{ borderColor: "var(--tm-border)" }}
      >
        © ThinkMyWay — built for the Emergent Builder Fest 2026.
      </footer>
    </main>
  );
}
